import { Close, FilterAlt } from '@mui/icons-material'
import {
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import type { Contact } from '@otw/models/leads/contacts'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import {
  buildAudienceEvalContext,
  cloneAudienceFilterGroup,
  emptyAudienceFilter,
  parseAudienceFilterGroup,
  parseReplyToUserIds,
  previewAudienceEligibility,
  type AudienceFilterGroup,
  type MarketingAudience,
} from '@otw/models/leads/marketingCampaign'
import type { Terminal } from '@otw/models/leads/terminals'
import type { PickupLocation, PrivateUserInfoType } from '@otw/models'
import React, { useEffect, useMemo, useState } from 'react'
import { useMarketingAudiences } from 'hooks/contacts/useMarketingAudiences'
import AudienceEligibilitySummary from './AudienceEligibilitySummary'
import AudienceFilterBuilder from './AudienceFilterBuilder'

const contactOptionLabel = (contact: Contact): string => {
  const name = contact.name.trim() || 'Unnamed contact'
  const email = contact.email?.trim()
  return email ? `${name} · ${email}` : name
}

const contactsByIds = (contacts: Contact[], ids: string[]): Contact[] => {
  const byId = new Map(contacts.map((contact) => [contact.id, contact]))
  return ids.flatMap((id) => {
    const contact = byId.get(id)
    return contact ? [contact] : []
  })
}

interface AudienceFormDialogProps {
  open: boolean
  onClose: () => void
  audience: MarketingAudience | null
  contacts: Contact[]
  companies: ContactCompany[]
  pickupLocations: PickupLocation[]
  terminals: Terminal[]
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
}

const AudienceFormDialog: React.FC<AudienceFormDialogProps> = ({
  open,
  onClose,
  audience,
  contacts,
  companies,
  pickupLocations,
  terminals,
  salesOwners,
  inventoryOwners,
}) => {
  const theme = useTheme()
  const accent = theme.palette.info.main
  const { createAudience, updateAudience, isSaving } = useMarketingAudiences()
  const [name, setName] = useState('')
  const [filter, setFilter] = useState<AudienceFilterGroup>(emptyAudienceFilter())
  const [includedContactIds, setIncludedContactIds] = useState<string[]>([])
  const [excludedContactIds, setExcludedContactIds] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    if (audience) {
      setName(audience.name)
      setFilter(cloneAudienceFilterGroup(parseAudienceFilterGroup(audience.filter)))
      setIncludedContactIds(parseReplyToUserIds(audience.includedContactIds))
      setExcludedContactIds(parseReplyToUserIds(audience.excludedContactIds))
    } else {
      setName('')
      setFilter(emptyAudienceFilter())
      setIncludedContactIds([])
      setExcludedContactIds([])
    }
  }, [open, audience])

  const evalContext = useMemo(
    () => buildAudienceEvalContext({ companies, pickupLocations }),
    [companies, pickupLocations],
  )

  const preview = useMemo(
    () =>
      previewAudienceEligibility(contacts, filter, evalContext, {
        includedContactIds,
        excludedContactIds,
      }),
    [contacts, evalContext, excludedContactIds, filter, includedContactIds],
  )

  const canSave = name.trim().length > 0 && !isSaving

  const handleSave = async () => {
    if (!canSave) return
    if (audience) {
      await updateAudience({
        id: audience.id,
        name: name.trim(),
        filter,
        includedContactIds,
        excludedContactIds,
      })
    } else {
      await createAudience({
        name: name.trim(),
        filter,
        includedContactIds,
        excludedContactIds,
      })
    }
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '92vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.18)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{ bgcolor: alpha(accent, 0.18), color: accent, width: 44, height: 44 }}
          >
            <FilterAlt />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: accent, letterSpacing: 1.2, fontWeight: 800, fontSize: '1rem' }}
            >
              {audience ? 'Edit audience' : 'New audience'}
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'block', lineHeight: 1.4 }}
            >
              Rules stay live. Handpicked and excluded people stay on the list until you change them.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={isSaving}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(accent, 0.08), color: accent },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1.5, md: 3 } }} dividers={false}>
        <Stack spacing={2.5} sx={{ pt: { xs: 2, md: 2.5 } }}>
          <TextField
            label="Audience name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
            disabled={isSaving}
            placeholder="e.g. Texas customers, no activity in 90 days"
          />
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Who is included
            </Typography>
            <AudienceFilterBuilder
              value={filter}
              onChange={setFilter}
              salesOwners={salesOwners}
              inventoryOwners={inventoryOwners}
              terminals={terminals}
              disabled={isSaving}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              disabled={isSaving}
              options={contacts.filter((contact) => !excludedContactIds.includes(contact.id))}
              value={contactsByIds(contacts, includedContactIds)}
              onChange={(_, next) => setIncludedContactIds(next.map((contact) => contact.id))}
              getOptionLabel={contactOptionLabel}
              isOptionEqualToValue={(left, right) => left.id === right.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return (
                    <Chip key={key} {...tagProps} size="small" label={contactOptionLabel(option)} />
                  )
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Handpick contacts" placeholder="Add specific people" />
              )}
            />
            <FormHelperText sx={{ mx: 0, mt: 0.75 }}>
              With no filter rules, only these people are included. Otherwise they are added on top of the rules.
            </FormHelperText>
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              disabled={isSaving}
              options={contacts.filter((contact) => !includedContactIds.includes(contact.id))}
              value={contactsByIds(contacts, excludedContactIds)}
              onChange={(_, next) => setExcludedContactIds(next.map((contact) => contact.id))}
              getOptionLabel={contactOptionLabel}
              isOptionEqualToValue={(left, right) => left.id === right.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return (
                    <Chip key={key} {...tagProps} size="small" label={contactOptionLabel(option)} />
                  )
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Exclude contacts" placeholder="Leave these people out" />
              )}
            />
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              bgcolor: alpha(theme.palette.background.default, 0.6),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Recipients
            </Typography>
            <AudienceEligibilitySummary preview={preview} />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}
      >
        <Button onClick={onClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} variant="contained" disabled={!canSave}>
          {isSaving ? 'Saving…' : audience ? 'Save audience' : 'Create audience'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AudienceFormDialog
